import NiceModal from '@ebay/nice-modal-react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { type ChangeOrderRefType, getChangeOrderApprovals } from '@/api/Sherpa/ChangeOrder';
import { Layout } from '@/components/Layout';
import { LayoutCard, LayoutCardTitle } from '@/components/LayoutCard';
import { Accordion, AccordionItem } from '@/components/UI/Accordion';
import Badge from '@/components/UI/Badge';
import DropDown, { type DropDownMenuItem } from '@/components/UI/DropDown';
import { approvalAction } from '@/constants/Approval';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { ApprovalDetails } from '@/features/ChangeOrder/Details/Approvals/ApprovalDetails';
import { ApproveModal } from '@/features/ChangeOrder/Details/Approvals/ApproveModal';
import { RejectModal } from '@/features/ChangeOrder/Details/Approvals/RejectModal';
import type { ChangeOrderType } from '@/features/ChangeOrder/types';
import { authSelector } from '@/store';
import { convertEnumToText, formatDate3 } from '@/utils';
import { DelegateModal } from './DelegateModal';

interface IApprovalsProps {
  type: ChangeOrderType;
  refId: number;
  refType: ChangeOrderRefType;
  queryKey: string;
  title: string;
}

const Approvals = ({ refId, refType, type, queryKey, title }: IApprovalsProps) => {
  const auth = useSelector(authSelector);
  const { projectId, changeOrderId } = useParams();
  const queryClient = useQueryClient();

  const { data: approvals } = useQuery(
    [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_APPROVALS, changeOrderId],
    () => getChangeOrderApprovals({ refType, refId }),
    {
      select: (data) => data.data,
    }
  );

  const latestApproval = approvals?.[0];
  const { status: approvalStatusValue, assignUser } = latestApproval || {};

  const isSuperAdmin = ['Superadmin', 'Super Admin'].includes(auth.data.role);
  const hasAccessForApproval =
    approvalStatusValue !== 'APPROVED' && approvalStatusValue !== 'REJECTED' && (isSuperAdmin || assignUser?.id === auth.data?.id);

  const invalidateQueries = () => {
    queryClient.getQueryCache();
    queryClient.invalidateQueries([queryKey, Number(changeOrderId)]);
    queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_APPROVALS, changeOrderId]);
    queryClient.invalidateQueries([`${refType}-audit`]);
  };

  const menuItems: DropDownMenuItem[] = Object.keys(approvalAction).map((key) => {
    return {
      clicked: () => {
        if (key === 'DELEGATED') {
          NiceModal.show(DelegateModal, {
            title,
            projectId,
            assignedUser: assignUser,
            approvalId: latestApproval?.id,
            onSubmitted: invalidateQueries,
          });
        } else if (key === 'REJECTED') {
          NiceModal.show(RejectModal, {
            title,
            approvalId: latestApproval?.id,
            onSubmitted: invalidateQueries,
          });
        } else if (key === 'APPROVED') {
          NiceModal.show(ApproveModal, {
            title,
            projectId,
            approvalId: latestApproval?.id,
            isBudget: type === 'budget',
            onSubmitted: invalidateQueries,
          });
        }
      },
      name: approvalAction[key as keyof typeof approvalAction],
    };
  });

  return (
    <Layout className='shadow rounded-lg'>
      <LayoutCard>
        <LayoutCardTitle
          title='Approvals'
          actions={
            <div className='flex gap-2 items-center'>
              {hasAccessForApproval && (
                <DropDown menuItems={menuItems}>
                  <div className='flex gap-2 items-center border p-2 rounded-md w-40 justify-between font-medium'>
                    <span>Actions</span>
                    <ChevronDownIcon className='h-4 w-4' />
                  </div>
                </DropDown>
              )}
            </div>
          }
        />
        <div className='h-screen/2 overflow-y-scroll p-2'>
          <Accordion>
            {approvals?.map((approval) => {
              return (
                <AccordionItem
                  key={String(approval.id)}
                  id={String(approval.id)}
                  title={
                    <div className='flex justify-between pr-2'>
                      <h3 className=''>Version: {approval.version}</h3>
                      <Badge status={approval.status} text={convertEnumToText(approval.status)} />
                    </div>
                  }
                >
                  <div className='space-y-3'>
                    <div className='flex justify-between items-start'>
                      <div className='space-y-3'>
                        <div className='flex justify-between items-center gap-3'>
                          <span className='text-xs text-neutralColor-normal'>Assigned To: {approval.assignUser?.name}</span>
                          <span className='text-xs text-neutralColor-normal'>Assigned Date: {formatDate3(approval.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <Accordion>
                      <AccordionItem id='notes-version' title={<h4 className='font-medium'>Notes</h4>}>
                        {approval.note ?? 'None'}
                      </AccordionItem>
                    </Accordion>
                    <Accordion multi>
                      <AccordionItem
                        triggerClassName='p-4'
                        id={`${type}-version`}
                        customClassName='py-0'
                        title={
                          <div className='flex justify-between items-start'>
                            <h4 className='font-medium'>Documents</h4>
                            <span>{approval.approvalAsset?.length}</span>
                          </div>
                        }
                      >
                        <ApprovalDetails files={approval?.approvalAsset} uploadedBy={approval?.createdBy?.name} />
                      </AccordionItem>
                    </Accordion>
                  </div>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </LayoutCard>
    </Layout>
  );
};

export default Approvals;
